import { useState, useRef, useEffect, useCallback } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { sourcesApi, scansApi, scannersApi, credentialsApi, cbomApi, UploadOffsetMismatchError, UploadChunkTooLargeError } from '@/api/clients/apiClient'
import { useOrgStore } from '@/stores/orgStore'
import { ModalityBadge, StatusBadge, ScanStatusBadge } from '@/components/common/StatusBadge'
import { ProgressBar } from '@/components/common/ProgressBar'
import ScannerOptionsEditor from '@/components/common/ScannerOptionsEditor'
import { scannerOptionsFor } from '@/lib/scanner-options'
import type { CredentialType, ScannerInfo } from '@/api/types'
import { isLiveNetworkSource, liveMonitorScope, networkConfigOf } from '@/lib/live-network'
import { describeScanFrequency } from '@/lib/scan-schedule'

function formatDate(iso?: string) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString()
}

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-start gap-1">
      <dt className="text-sm font-medium text-subtle-light dark:text-subtle-dark w-44 shrink-0">
        {label}
      </dt>
      <dd className="text-sm text-text-light dark:text-text-dark break-all">{value}</dd>
    </div>
  )
}

// ── Credential Modal ──────────────────────────────────────────────────────────

interface CredentialModalProps {
  orgId: string
  sourceId: string
  currentCredentialId?: string | null
  onClose: () => void
}

function CredentialModal({ orgId, sourceId, currentCredentialId, onClose }: CredentialModalProps) {
  const queryClient = useQueryClient()
  const [credMode, setCredMode] = useState<'select' | 'new'>('select')
  const [selectedCredId, setSelectedCredId] = useState(currentCredentialId ?? '')
  const [credSearch, setCredSearch] = useState('')
  const [credDropOpen, setCredDropOpen] = useState(false)
  const credDropRef = useRef<HTMLDivElement>(null)
  const [newCredName, setNewCredName] = useState('')
  const [newCredType, setNewCredType] = useState<CredentialType>('token')
  const [newCredData, setNewCredData] = useState('')
  const [newCredHint, setNewCredHint] = useState('')
  const [error, setError] = useState('')

  const { data: existingCreds = [] } = useQuery({
    queryKey: ['credentials', orgId],
    queryFn: () => credentialsApi.list(orgId),
    enabled: !!orgId,
  })

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (credDropRef.current && !credDropRef.current.contains(e.target as Node)) {
        setCredDropOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const filteredCreds = existingCreds.filter(c => {
    const q = credSearch.toLowerCase()
    return c.name.toLowerCase().includes(q) || c.credential_type.toLowerCase().includes(q) || (c.hint ?? '').toLowerCase().includes(q)
  })

  const selectedCred = existingCreds.find(c => c.id === selectedCredId)

  const mutation = useMutation({
    mutationFn: (req: { credential_id: string }) => sourcesApi.setCredential(orgId, sourceId, req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['source', orgId, sourceId] })
      queryClient.invalidateQueries({ queryKey: ['sources', orgId] })
      queryClient.invalidateQueries({ queryKey: ['credentials', orgId] })
      onClose()
    },
    onError: (err: Error) => setError(err.message),
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    if (credMode === 'select') {
      if (!selectedCredId) { setError('Select a credential'); return }
      mutation.mutate({ credential_id: selectedCredId })
    } else {
      if (!newCredData.trim()) { setError('Credential data required'); return }
      try {
        const newCred = await credentialsApi.create(orgId, {
          name: newCredName.trim() || 'Credential',
          credential_type: newCredType,
          data: newCredData,
          hint: newCredHint,
        })
        queryClient.invalidateQueries({ queryKey: ['credentials', orgId] })
        mutation.mutate({ credential_id: newCred.id })
      } catch (err) {
        setError((err as Error).message)
      }
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] rounded-xl shadow-float dark:shadow-none dark:border dark:border-border-dark w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border-light dark:border-border-dark">
          <h2 className="text-lg font-semibold text-text-light dark:text-text-dark">Set Credential</h2>
          <button onClick={onClose} className="text-subtle-light dark:text-subtle-dark">
            <span className="material-icons">close</span>
          </button>
        </div>

        <div className="flex border-b border-border-light dark:border-border-dark">
          {(['select', 'new'] as const).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setCredMode(m)}
              className={`flex-1 py-2.5 text-sm font-medium transition-colors ${
                credMode === m
                  ? 'text-primary border-b-2 border-primary'
                  : 'text-subtle-light dark:text-subtle-dark hover:text-text-light dark:hover:text-text-dark'
              }`}
            >
              {m === 'select' ? 'Select Existing' : 'Create New'}
            </button>
          ))}
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <p className="text-sm text-error bg-red-50 dark:bg-red-900/20 p-3 rounded-lg">{error}</p>
          )}

          {credMode === 'select' ? (
            <div>
              <label className="block text-sm font-medium text-text-light dark:text-text-dark mb-1">
                Credential
              </label>
              {existingCreds.length === 0 ? (
                <p className="text-sm text-subtle-light dark:text-subtle-dark py-2">
                  No credentials yet. Switch to "Create New" to create one.
                </p>
              ) : (
                <div ref={credDropRef} className="relative">
                  <div
                    className="w-full flex items-center gap-2 pl-3 pr-3 py-2 text-sm rounded-lg bg-background-light dark:bg-background-dark cursor-pointer"
                    onClick={() => { setCredDropOpen(o => !o); setCredSearch('') }}
                  >
                    {credDropOpen ? (
                      <input
                        autoFocus
                        value={credSearch}
                        onChange={(e) => setCredSearch(e.target.value)}
                        onKeyDown={(e) => e.key === 'Escape' && setCredDropOpen(false)}
                        placeholder="Search credentials…"
                        className="flex-1 bg-transparent outline-none focus:outline-none focus:ring-0 focus:border-none border-none ring-0 p-0 m-0 text-sm text-text-light dark:text-text-dark placeholder:text-subtle-light dark:placeholder:text-subtle-dark"
                        onClick={(e) => e.stopPropagation()}
                      />
                    ) : (
                      <span className={`flex-1 truncate ${selectedCred ? 'text-text-light dark:text-text-dark' : 'text-subtle-light dark:text-subtle-dark'}`}>
                        {selectedCred ? `${selectedCred.name} · ${selectedCred.credential_type}${selectedCred.hint ? ` (${selectedCred.hint})` : ''}` : '— select a credential —'}
                      </span>
                    )}
                    <span className="material-icons text-sm text-subtle-light dark:text-subtle-dark">{credDropOpen ? 'expand_less' : 'expand_more'}</span>
                  </div>
                  {credDropOpen && (
                    <div className="absolute z-10 mt-1 w-full bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] rounded-lg shadow-float dark:shadow-none max-h-48 overflow-y-auto">
                      {filteredCreds.length === 0 ? (
                        <p className="px-3 py-2 text-sm text-subtle-light dark:text-subtle-dark">No matches</p>
                      ) : filteredCreds.map(c => (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => { setSelectedCredId(c.id); setCredDropOpen(false) }}
                          className={`w-full text-left px-3 py-2 text-sm hover:bg-background-light dark:hover:bg-background-dark transition-colors ${c.id === selectedCredId ? 'text-primary font-medium' : 'text-text-light dark:text-text-dark'}`}
                        >
                          <span className="font-medium">{c.name}</span>
                          <span className="text-subtle-light dark:text-subtle-dark ml-1 font-mono text-xs">· {c.credential_type}{c.hint ? ` (${c.hint})` : ''}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <>
              <div>
                <label className="block text-sm font-medium text-text-light dark:text-text-dark mb-1">Name</label>
                <input
                  value={newCredName}
                  onChange={(e) => setNewCredName(e.target.value)}
                  placeholder="Credential name"
                  className="w-full px-3 py-2 text-sm rounded-lg bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-light dark:text-text-dark mb-1">Type</label>
                <select
                  value={newCredType}
                  onChange={(e) => setNewCredType(e.target.value as CredentialType)}
                  className="w-full pl-3 pr-10 py-2 text-sm rounded-lg bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark"
                >
                  <option value="token">Token</option>
                  <option value="ssh-key">SSH Key</option>
                  <option value="basic">Basic (user:pass)</option>
                  <option value="iam-role">IAM Role</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-text-light dark:text-text-dark mb-1">
                  Credential Data
                </label>
                <textarea
                  value={newCredData}
                  onChange={(e) => setNewCredData(e.target.value)}
                  rows={3}
                  placeholder={newCredType === 'ssh-key' ? 'Paste SSH private key' : 'Credential value'}
                  className="w-full px-3 py-2 text-sm rounded-lg bg-background-light dark:bg-background-dark font-mono resize-none text-text-light dark:text-text-dark"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-light dark:text-text-dark mb-1">
                  Hint (optional)
                </label>
                <input
                  value={newCredHint}
                  onChange={(e) => setNewCredHint(e.target.value)}
                  placeholder="e.g. github-token or deploy-key"
                  className="w-full px-3 py-2 text-sm rounded-lg bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark"
                />
              </div>
            </>
          )}

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 text-sm rounded-lg text-text-light dark:text-text-dark"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={mutation.isPending || (credMode === 'select' && existingCreds.length === 0)}
              className="flex-1 px-4 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50"
            >
              {mutation.isPending ? 'Saving…' : 'Save Credential'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ── Scanner read card ─────────────────────────────────────────────────────────

interface ScannerReadCardProps {
  scanner: ScannerInfo | null
  scannerId?: string
  isDefault: boolean
  isConfigured: boolean
}

function ScannerReadCard({ scanner, scannerId, isDefault, isConfigured }: ScannerReadCardProps) {
  const id = scanner?.id ?? scannerId ?? '—'

  return (
    <div
      className={`rounded-lg border overflow-hidden ${
        isConfigured
          ? 'border-primary/40 bg-background-light dark:bg-background-dark'
          : 'border-border-light dark:border-border-dark bg-background-light dark:bg-background-dark'
      }`}
    >
      {/* Header row */}
      <div className="flex items-center gap-2.5 px-3 py-2.5">
        <span className={`material-icons text-base shrink-0 ${isConfigured ? 'text-primary' : 'text-subtle-light dark:text-subtle-dark'}`}>
          memory
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-mono text-text-light dark:text-text-dark">{id}</p>
          {scanner?.metadata?.description && (
            <p className="text-xs text-subtle-light dark:text-subtle-dark truncate mt-0.5">
              {scanner.metadata.description}
            </p>
          )}
          {!scanner && (
            <p className="text-xs text-warning mt-0.5">Not found in registry</p>
          )}
          {scanner?.metadata?.tags && scanner.metadata.tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1.5">
              {scanner.metadata.tags.slice(0, 5).map((tag) => (
                <span
                  key={tag}
                  className="text-xs px-1.5 py-0.5 rounded bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] text-subtle-light dark:text-subtle-dark"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {isDefault && (
            <span className="text-xs px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
              default
            </span>
          )}
        </div>
      </div>

    </div>
  )
}

// ── Upload helpers ────────────────────────────────────────────────────────────

const ARCHIVE_RE = /\.(zip|tar(\.gz|\.bz2|\.xz)?|tgz|tbz2|txz|rar|7z)$/i
const CAPTURE_RE = /\.(pcap|pcapng|cap|snoop|har)$/i
// Fallback for servers that do not advertise chunk_size on the upload session.
const CHUNK_SIZE = 5 * 1024 * 1024 // 5 MB
// Smallest chunk tried after a proxy rejects one with 413; matches the
// server's minimum for QTZ_SCANNER_UPLOAD_CHUNK_SIZE.
const MIN_CHUNK_SIZE = 64 * 1024 // 64 KiB

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / (1024 * 1024)).toFixed(1)} MB`
}

async function traverseEntry(
  entry: FileSystemEntry,
  path: string,
  map: Record<string, Uint8Array>
): Promise<void> {
  if (entry.isFile) {
    const file = await new Promise<File>((res, rej) =>
      (entry as FileSystemFileEntry).file(res, rej)
    )
    map[path] = new Uint8Array(await file.arrayBuffer())
    return
  }
  const reader = (entry as FileSystemDirectoryEntry).createReader()
  const children: FileSystemEntry[] = []
  await new Promise<void>((resolve, reject) => {
    const readBatch = () =>
      reader.readEntries((batch) => {
        if (!batch.length) { resolve(); return }
        children.push(...batch)
        readBatch()
      }, reject)
    readBatch()
  })
  await Promise.all(children.map((c) => traverseEntry(c, `${path}/${c.name}`, map)))
}

// ── Source Details Page ───────────────────────────────────────────────────────

export default function SourceDetails() {
  const { id } = useParams<{ id: string }>()
  const { currentOrgId } = useOrgStore()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const folderInputRef = useRef<HTMLInputElement>(null)
  const [showCredModal, setShowCredModal] = useState(false)
  const [uploadMsg, setUploadMsg] = useState('')
  const [isZipping, setIsZipping] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadBytesUploaded, setUploadBytesUploaded] = useState(0)
  const [uploadTotalBytes, setUploadTotalBytes] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const uploadAbortRef = useRef(false)
  const uploadSessionIdRef = useRef<string | null>(null)
  const bomFileInputRef = useRef<HTMLInputElement>(null)
  const [bomStartedAt, setBomStartedAt] = useState('')
  const [bomDurationSec, setBomDurationSec] = useState('')
  const [importBomStatus, setImportBomStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null)
  const [bomIsDragging, setBomIsDragging] = useState(false)
const [editingScanners, setEditingScanners] = useState(false)
  const [draftScannerIds, setDraftScannerIds] = useState<string[]>([])
  const [draftScannerOptions, setDraftScannerOptions] = useState<Record<string, Record<string, unknown>>>({})

  const { data: sourceResp, isLoading } = useQuery({
    queryKey: ['source', currentOrgId, id],
    queryFn: () => sourcesApi.get(currentOrgId, id!),
    enabled: !!id,
  })

  const { data: allCreds = [] } = useQuery({
    queryKey: ['credentials', currentOrgId],
    queryFn: () => credentialsApi.list(currentOrgId),
    enabled: !!currentOrgId,
  })

  const { data: scansResp } = useQuery({
    queryKey: ['scans', currentOrgId, 'source', id],
    queryFn: () => scansApi.list(currentOrgId, { source_id: id }),
    enabled: !!id,
    refetchInterval: (query) => {
      const scans = query.state.data?.data ?? []
      return scans.some((s) => s.status === 'running' || s.status === 'pending') ? 1000 : false
    },
  })

  const { data: scannersResp } = useQuery({
    queryKey: ['scanners'],
    queryFn: () => scannersApi.list(),
    staleTime: 1000 * 60 * 5,
  })

  const deleteMutation = useMutation({
    mutationFn: () => sourcesApi.delete(currentOrgId, id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sources', currentOrgId] })
      navigate('/sources')
    },
  })

  const deleteCredMutation = useMutation({
    mutationFn: () => sourcesApi.deleteCredential(currentOrgId, id!),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['source', currentOrgId, id] }),
  })

  const scanMutation = useMutation({
    mutationFn: (opts?: Record<string, Record<string, unknown>>) =>
      scansApi.create(currentOrgId, {
        source_id: id!,
        options: opts && Object.keys(opts).length > 0 ? opts : undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scans', currentOrgId] })
      queryClient.refetchQueries({ queryKey: ['scans', currentOrgId, 'source', id] })
    },
  })

  const scannerUpdateMutation = useMutation({
    mutationFn: ({ scanner_ids, scanner_options }: { scanner_ids: string[]; scanner_options: Record<string, Record<string, unknown>> }) =>
      sourcesApi.update(currentOrgId, id!, {
        scanner_ids,
        scanner_options: Object.keys(scanner_options).length > 0 ? scanner_options : undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['source', currentOrgId, id] })
      setEditingScanners(false)
    },
  })

  const importBomMutation = useMutation({
    mutationFn: async (file: File) => {
      const bom = JSON.parse(await file.text())
      const body: { bom: unknown; started_at?: string; duration_ms?: number } = { bom }
      if (bomStartedAt) {
        body.started_at = new Date(bomStartedAt).toISOString()
        if (bomDurationSec) body.duration_ms = parseFloat(bomDurationSec) * 1000
      }
      return cbomApi.importBom(currentOrgId, id!, body as never)
    },
    onSuccess: (result) => {
      const { assets_loaded, packages_loaded, new_assets, removed_assets, changed_assets } = result
      const delta = new_assets != null
        ? ` (+${new_assets} new, -${removed_assets ?? 0} removed, ~${changed_assets ?? 0} changed)`
        : ''
      setImportBomStatus({
        type: 'success',
        message: `Imported CycloneDX BOM. ${assets_loaded} crypto assets, ${packages_loaded} packages loaded.${delta}`,
      })
      queryClient.invalidateQueries({ queryKey: ['scans', currentOrgId] })
      queryClient.refetchQueries({ queryKey: ['scans', currentOrgId, 'source', id] })
      queryClient.invalidateQueries({ queryKey: ['asset-summary-per-source', currentOrgId] })
      queryClient.invalidateQueries({ queryKey: ['asset-trend', currentOrgId] })
      queryClient.invalidateQueries({ queryKey: ['org-findings', currentOrgId] })
    },
    onError: (err: Error) => setImportBomStatus({ type: 'error', message: err.message }),
  })

  const startEditingScanners = useCallback((currentIds: string[], currentOpts: Record<string, Record<string, unknown>>) => {
    setDraftScannerIds(currentIds)
    setDraftScannerOptions(currentOpts)
    setEditingScanners(true)
  }, [])

  const doChunkedUpload = async (file: File) => {
    setIsUploading(true)
    setUploadMsg('')
    setUploadBytesUploaded(0)
    setUploadTotalBytes(file.size)
    uploadAbortRef.current = false

    let sessionId: string
    let chunkSize = CHUNK_SIZE
    let shrunk = false // a proxy forced smaller chunks (413) during this upload
    try {
      const session = await sourcesApi.createUploadSession(currentOrgId, id!, file.size, file.name)
      sessionId = session.session_id
      if (session.chunk_size && session.chunk_size > 0) chunkSize = session.chunk_size
      uploadSessionIdRef.current = sessionId
    } catch (err) {
      setUploadMsg(`Upload failed: ${(err as Error).message}`)
      setIsUploading(false)
      return
    }

    let offset = 0
    const totalSize = file.size

    while (offset < totalSize) {
      if (uploadAbortRef.current) {
        try { await sourcesApi.cancelUploadSession(currentOrgId, id!, sessionId) } catch { /* ignore */ }
        setUploadMsg('Upload cancelled.')
        setIsUploading(false)
        uploadSessionIdRef.current = null
        return
      }

      const end = Math.min(offset + chunkSize, totalSize)
      const chunk = await file.slice(offset, end).arrayBuffer()

      let retries = 3
      let chunkDone = false
      while (retries > 0) {
        try {
          const result = await sourcesApi.uploadChunk(currentOrgId, id!, sessionId, chunk, offset)
          offset = result.newOffset
          setUploadBytesUploaded(offset)
          if (result.done) {
            queryClient.invalidateQueries({ queryKey: ['scans', currentOrgId] })
            queryClient.refetchQueries({ queryKey: ['scans', currentOrgId, 'source', id] })
            setUploadMsg(shrunk
              ? `Upload complete — scan triggered. A proxy rejected larger chunks (413), so ${formatBytes(chunkSize)} chunks were used; set QTZ_SCANNER_UPLOAD_CHUNK_SIZE to at most that to skip the retries.`
              : 'Upload complete — scan triggered.')
            setIsUploading(false)
            uploadSessionIdRef.current = null
            return
          }
          chunkDone = true
          break
        } catch (err) {
          if (err instanceof UploadChunkTooLargeError) {
            // A proxy caps request bodies below our chunk size. Halve the chunk and
            // resend from the same offset; the smaller size sticks for the rest of
            // the upload. Give up only once even the minimum chunk is rejected.
            if (err.chunkBytes > MIN_CHUNK_SIZE) {
              chunkSize = Math.max(MIN_CHUNK_SIZE, Math.floor(err.chunkBytes / 2))
              shrunk = true
              chunkDone = true
              break
            }
            setUploadMsg(
              `Upload failed: a proxy rejected even a ${formatBytes(err.chunkBytes)} chunk as too large (413). ` +
              'Raise the proxy\'s client_max_body_size.'
            )
            setIsUploading(false)
            uploadSessionIdRef.current = null
            return
          }
          if (err instanceof UploadOffsetMismatchError) {
            // Recover by querying the real offset
            try {
              offset = await sourcesApi.getUploadSessionOffset(currentOrgId, id!, sessionId)
              setUploadBytesUploaded(offset)
              chunkDone = true
              break
            } catch {
              retries--
            }
          } else {
            retries--
            if (retries > 0) await new Promise((r) => setTimeout(r, 1000 * (4 - retries)))
          }
        }
      }

      if (!chunkDone) {
        setUploadMsg('Upload failed: too many retries.')
        setIsUploading(false)
        uploadSessionIdRef.current = null
        return
      }
    }

    setIsUploading(false)
  }

  const cancelUpload = async () => {
    uploadAbortRef.current = true
  }

  const doZipAndUpload = async (map: Record<string, Uint8Array>, name: string) => {
    setUploadMsg('')
    setIsZipping(true)
    let zipFile: File
    try {
      const { zipSync } = await import('fflate')
      const zipped = zipSync(map, { level: 1 })
      zipFile = new File([zipped as unknown as ArrayBuffer], `${name}.zip`, { type: 'application/zip' })
    } catch (err) {
      setUploadMsg(`Failed to compress: ${(err as Error).message}`)
      setIsZipping(false)
      return
    }
    setIsZipping(false)
    await doChunkedUpload(zipFile)
  }

  const handleFolderChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? [])
    if (folderInputRef.current) folderInputRef.current.value = ''
    if (files.length === 0) return
    const folderName = files[0].webkitRelativePath.split('/')[0] || 'upload'
    const map: Record<string, Uint8Array> = {}
    await Promise.all(files.map(async (f) => {
      map[f.webkitRelativePath || f.name] = new Uint8Array(await f.arrayBuffer())
    }))
    await doZipAndUpload(map, folderName)
  }

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    if (isUploading || isZipping) return
    setUploadMsg('')
    const isCapture = sourceResp?.data?.source_type === 'network-upload'
    const item = Array.from(e.dataTransfer.items).find((i) => i.kind === 'file')
    if (!item) return
    const entry = item.webkitGetAsEntry?.()
    if (entry?.isDirectory) {
      const map: Record<string, Uint8Array> = {}
      await traverseEntry(entry, entry.name, map)
      await doZipAndUpload(map, entry.name)
    } else {
      const file = item.getAsFile()
      if (!file) return
      const accepts = isCapture
        ? (CAPTURE_RE.test(file.name) || ARCHIVE_RE.test(file.name))
        : ARCHIVE_RE.test(file.name)
      if (accepts) {
        await doChunkedUpload(file)
      } else {
        setUploadMsg(isCapture
          ? 'Please drop a capture (.pcap, .pcapng, .cap, .snoop, .har) or an archive/folder containing captures.'
          : 'Please drop an archive (.zip, .tar.gz, .rar, .7z, …) or a folder.')
      }
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <span className="material-icons animate-spin text-primary text-4xl">sync</span>
      </div>
    )
  }

  const source = sourceResp?.data
  if (!source) {
    return (
      <div className="text-center py-20">
        <p className="text-subtle-light dark:text-subtle-dark">Discovery source not found.</p>
        <Link to="/sources" className="text-primary hover:underline text-sm mt-2 inline-block">
          ← Back to Discovery Sources
        </Link>
      </div>
    )
  }

  const scans = scansResp?.data ?? []
  const hasActiveJob = scans.some((s) => s.status === 'running' || s.status === 'pending')
  const allScanners: ScannerInfo[] = scannersResp?.data ?? []
  const isBinary = source.modality === 'binary'
  const isManual = source.modality === 'manual' || source.modality === 'binary' || source.source_type === 'source-upload' || source.source_type === 'binary-upload' || source.source_type === 'network-upload'
  // A live monitor has nothing for the server to connect to: its agent watches
  // traffic on its host and uploads what it sees. The server rejects a scan
  // trigger for one, so the button is disabled rather than offering an action
  // that can only fail.
  const isLive = isLiveNetworkSource(source)
  const liveScope = liveMonitorScope(networkConfigOf(source))
  const uploadVariant: 'archive' | 'capture' = source.source_type === 'network-upload' ? 'capture' : 'archive'
  const configuredScannerIds = source.scanner_ids ?? []
  const defaultScanners = allScanners.filter((s) => s.default_for.includes(source.source_type))
  const compatibleScanners = allScanners.filter((s) => s.modalities.includes(source.modality))

  return (
    <div className="space-y-6">
      {showCredModal && (
        <CredentialModal
          orgId={currentOrgId}
          sourceId={source.id}
          currentCredentialId={source.credential_id}
          onClose={() => setShowCredModal(false)}
        />
      )}

      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-subtle-light dark:text-subtle-dark">
        <Link to="/sources" className="hover:text-primary">
          Sources
        </Link>
        <span className="material-icons text-base">chevron_right</span>
        <span className="text-text-light dark:text-text-dark">{source.name}</span>
      </div>

      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold text-text-light dark:text-text-dark">
                {source.name}
              </h1>
              <ModalityBadge modality={source.modality} />
              {isLive && (
                <span
                  className="inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-medium rounded-full bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300"
                  title="Fed continuously by a monitoring agent on the host"
                >
                  <span className="relative flex h-2 w-2 shrink-0">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-current opacity-75" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-current" />
                  </span>
                  Live monitor
                </span>
              )}
              <StatusBadge
                label={source.is_active ? 'Active' : 'Inactive'}
                variant={source.is_active ? 'success' : 'neutral'}
              />
            </div>
            {isLive && (
              <p className="text-sm text-subtle-light dark:text-subtle-dark mt-0.5">
                {liveScope
                  ? `Passive monitor on ${liveScope} — its agent uploads what it observes.`
                  : 'Passive monitor — waiting for its agent\u2019s first upload.'}
              </p>
            )}
            {source.description && (
              <p className="text-sm text-subtle-light dark:text-subtle-dark mt-0.5">
                {source.description}
              </p>
            )}
          </div>
        </div>
        <div className="flex gap-3">
          <Link
            to={`/sources/${source.id}/sbom-history`}
            className="flex items-center gap-1.5 px-4 py-2 text-sm rounded-lg hover:border-primary hover:text-primary text-subtle-light dark:text-subtle-dark"
          >
            <span className="material-icons text-base">history</span>
            SBOM History
          </Link>
          <button
            onClick={() => {
              if (confirm(`Delete source "${source.name}"?`)) deleteMutation.mutate()
            }}
            className="flex items-center gap-1.5 px-4 py-2 text-sm border border-error/30 text-error rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20"
          >
            <span className="material-icons text-base">delete</span>
            Delete
          </button>
          <button
            onClick={() => scanMutation.mutate(source.scanner_options)}
            disabled={scanMutation.isPending || hasActiveJob || isManual || isLive}
            title={
              isLive ? 'This source is updated by its monitoring agent\u2019s uploads, not by server-initiated scans' :
              isManual ? 'Upload a file to trigger a scan for this source' :
              hasActiveJob ? 'A scan is already running for this source' : undefined
            }
            className="flex items-center gap-1.5 px-4 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50"
          >
            {scanMutation.isPending ? (
              <>
                <span className="material-icons text-base animate-spin">sync</span>
                Starting…
              </>
            ) : (
              <>
                <span className="material-icons text-base">play_arrow</span>
                Run Scan Now
              </>
            )}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Source Config */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] rounded-xl shadow-ambient dark:shadow-none border border-border-light dark:border-border-dark p-6">
            <h2 className="text-base font-semibold text-text-light dark:text-text-dark mb-4">
              Source Configuration
            </h2>
            <dl className="space-y-3">
              <InfoRow label="ID" value={<code className="text-xs font-mono">{source.id}</code>} />
              <InfoRow label="Modality" value={<ModalityBadge modality={source.modality} />} />
              <InfoRow
                label="Source Type"
                value={<code className="text-xs font-mono">{source.source_type}</code>}
              />
              <InfoRow label="Scan Frequency" value={describeScanFrequency(source)} />
              <InfoRow label="Last Scanned" value={formatDate(source.last_scanned_at)} />
              {source.last_scanned_commit && (
                <InfoRow
                  label="Last Commit"
                  value={
                    <code className="text-xs font-mono">{source.last_scanned_commit.slice(0, 8)}</code>
                  }
                />
              )}
              <InfoRow label="Created" value={formatDate(source.created_at)} />
              <InfoRow label="Updated" value={formatDate(source.updated_at)} />
            </dl>

            {source.config && Object.keys(source.config).length > 0 && (
              <>
                <div className="my-4 border-t border-border-light dark:border-border-dark" />
                <h3 className="text-sm font-medium text-text-light dark:text-text-dark mb-3">
                  Type-specific Config
                </h3>
                <pre className="text-xs font-mono bg-background-light dark:bg-background-dark rounded-lg p-4 overflow-auto max-h-48 text-subtle-light dark:text-subtle-dark">
                  {JSON.stringify(source.config, null, 2)}
                </pre>
              </>
            )}
          </div>

          {/* Scanners */}
          <div className="bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] rounded-xl shadow-ambient dark:shadow-none border border-border-light dark:border-border-dark p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-semibold text-text-light dark:text-text-dark">
                Scanners
              </h2>
              {!editingScanners && (
                <button
                  onClick={() => startEditingScanners(configuredScannerIds, source.scanner_options ?? {})}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg hover:border-primary hover:text-primary text-subtle-light dark:text-subtle-dark"
                >
                  <span className="material-icons text-sm">edit</span>
                  Configure
                </button>
              )}
            </div>

            {editingScanners ? (
              /* ── Edit mode ── */
              <div className="space-y-4">
                <p className="text-xs text-subtle-light dark:text-subtle-dark">
                  Select which scanners run for this source. Leave all unchecked to use the default
                  scanner for <code className="font-mono">{source.source_type}</code>.
                </p>

                {compatibleScanners.length === 0 ? (
                  <p className="text-xs italic text-subtle-light dark:text-subtle-dark">
                    No scanners registered for modality "{source.modality}".
                  </p>
                ) : (
                  <div className="space-y-2">
                    {compatibleScanners.map((s) => {
                      const isDefault = s.default_for.includes(source.source_type)
                      const checked = draftScannerIds.includes(s.id)
                      // Only the options that apply to this source type — the API
                      // rejects the rest, so offering them here would build a
                      // request that cannot be saved.
                      const applicable = scannerOptionsFor(s, source.source_type)
                      const hasOptions = checked && applicable.length > 0
                      return (
                        <div
                          key={s.id}
                          className={`rounded-lg border overflow-hidden transition-colors ${
                            checked
                              ? 'border-primary'
                              : 'border-border-light dark:border-border-dark'
                          }`}
                        >
                          {/* Scanner row */}
                          <label
                            className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors ${
                              checked
                                ? 'bg-primary/5'
                                : 'hover:bg-background-light dark:hover:bg-background-dark'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() =>
                                setDraftScannerIds((prev) =>
                                  prev.includes(s.id)
                                    ? prev.filter((x) => x !== s.id)
                                    : [...prev, s.id]
                                )
                              }
                              className="accent-primary shrink-0"
                            />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-mono text-text-light dark:text-text-dark">
                                {s.id}
                              </p>
                              {s.metadata?.description ? (
                                <p className="text-xs text-subtle-light dark:text-subtle-dark truncate mt-0.5">
                                  {s.metadata.description}
                                </p>
                              ) : s.modalities.length > 0 ? (
                                <p className="text-xs text-subtle-light dark:text-subtle-dark mt-0.5">
                                  {s.modalities.join(', ')}
                                </p>
                              ) : null}
                              {s.metadata?.tags && s.metadata.tags.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-1.5">
                                  {s.metadata.tags.slice(0, 5).map((tag) => (
                                    <span
                                      key={tag}
                                      className="text-xs px-1.5 py-0.5 rounded bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] text-subtle-light dark:text-subtle-dark"
                                    >
                                      {tag}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                            <div className="flex items-center gap-1.5 shrink-0">
                              {applicable.length > 0 && !checked && (
                                <span className="text-xs text-subtle-light dark:text-subtle-dark">
                                  {applicable.length} opt{applicable.length !== 1 ? 's' : ''}
                                </span>
                              )}
                              {isDefault && (
                                <span className="text-xs px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
                                  default
                                </span>
                              )}
                            </div>
                          </label>

                          {/* Options panel — inside the same card */}
                          {hasOptions && (
                            <div className="border-t border-primary/20 bg-background-light dark:bg-background-dark px-4 py-3">
                              <p className="text-xs font-medium text-subtle-light dark:text-subtle-dark mb-3 uppercase tracking-wide">
                                Options
                              </p>
                              <ScannerOptionsEditor
                                scanner={s}
                                sourceType={source.source_type}
                                value={draftScannerOptions[s.id] ?? {}}
                                onChange={(v) =>
                                  setDraftScannerOptions((prev) => ({ ...prev, [s.id]: v }))
                                }
                              />
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                )}

                {scannerUpdateMutation.isError && (
                  <p className="text-xs text-error">
                    {(scannerUpdateMutation.error as Error)?.message ?? 'Save failed'}
                  </p>
                )}

                <div className="flex gap-2 pt-1">
                  <button
                    onClick={() => scannerUpdateMutation.mutate({ scanner_ids: draftScannerIds, scanner_options: draftScannerOptions })}
                    disabled={scannerUpdateMutation.isPending}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50"
                  >
                    {scannerUpdateMutation.isPending ? (
                      <>
                        <span className="material-icons text-sm animate-spin">sync</span>
                        Saving…
                      </>
                    ) : (
                      'Save'
                    )}
                  </button>
                  <button
                    onClick={() => setEditingScanners(false)}
                    className="px-3 py-1.5 text-sm rounded-lg hover:bg-background-light dark:hover:bg-background-dark text-text-light dark:text-text-dark"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              /* ── Read-only mode ── */
              <div className="space-y-2">
                {configuredScannerIds.length === 0 ? (
                  <>
                    <p className="text-sm text-subtle-light dark:text-subtle-dark mb-3">
                      No scanners explicitly configured — using the default scanner for{' '}
                      <code className="font-mono">{source.source_type}</code>.
                    </p>
                    {defaultScanners.map((s) => (
                      <ScannerReadCard
                        key={s.id}
                        scanner={s}
                        isDefault
                        isConfigured={false}
                      />
                    ))}
                  </>
                ) : (
                  configuredScannerIds.map((sid) => {
                    const info = allScanners.find((s) => s.id === sid)
                    const isDefault = info?.default_for.includes(source.source_type) ?? false
                    return (
                      <ScannerReadCard
                        key={sid}
                        scanner={info ?? null}
                        scannerId={sid}
                        isDefault={isDefault}
                        isConfigured
                      />
                    )
                  })
                )}

                {allScanners.length > 0 && (
                  <details className="pt-1">
                    <summary className="text-xs text-subtle-light dark:text-subtle-dark cursor-pointer hover:text-text-light dark:hover:text-text-dark select-none">
                      All registered scanners ({allScanners.length})
                    </summary>
                    <div className="mt-2 space-y-1.5">
                      {allScanners.map((s) => {
                        const active =
                          configuredScannerIds.length === 0
                            ? s.default_for.includes(source.source_type)
                            : configuredScannerIds.includes(s.id)
                        return (
                          <div
                            key={s.id}
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs transition-opacity ${
                              active
                                ? 'border-border-light dark:border-border-dark bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark'
                                : 'border-border-light/40 dark:border-border-dark/40 text-subtle-light dark:text-subtle-dark opacity-50'
                            }`}
                          >
                            <span className={`material-icons text-sm shrink-0 ${active ? 'text-primary' : ''}`}>
                              {active ? 'check_circle' : 'radio_button_unchecked'}
                            </span>
                            <span className="font-mono flex-1 truncate">{s.id}</span>
                            {s.metadata?.description && (
                              <span className="truncate opacity-75 hidden sm:block">{s.metadata.description}</span>
                            )}
                            <span className="shrink-0 opacity-60">{s.modalities.join(', ')}</span>
                          </div>
                        )
                      })}
                    </div>
                  </details>
                )}
              </div>
            )}
          </div>

          {/* Credentials */}
          <div className="bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] rounded-xl shadow-ambient dark:shadow-none border border-border-light dark:border-border-dark p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-semibold text-text-light dark:text-text-dark">
                Credentials
              </h2>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowCredModal(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-primary text-white rounded-lg hover:bg-primary/90"
                >
                  <span className="material-icons text-sm">lock</span>
                  Set Credential
                </button>
                <button
                  onClick={() => {
                    if (confirm('Delete credential for this source?'))
                      deleteCredMutation.mutate()
                  }}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg hover:bg-background-light dark:hover:bg-background-dark text-subtle-light dark:text-subtle-dark"
                >
                  <span className="material-icons text-sm">no_encryption</span>
                  Remove
                </button>
              </div>
            </div>
            {source.credential_id ? (() => {
              const cred = allCreds.find(c => c.id === source.credential_id)
              return (
                <div className="space-y-2">
                  <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
                    <div>
                      <span className="text-subtle-light dark:text-subtle-dark">Name: </span>
                      <span className="text-text-light dark:text-text-dark font-medium">{cred?.name ?? source.credential_name ?? '—'}</span>
                    </div>
                    {cred?.credential_type && (
                      <div>
                        <span className="text-subtle-light dark:text-subtle-dark">Type: </span>
                        <span className="font-mono text-xs text-text-light dark:text-text-dark">{cred.credential_type}</span>
                      </div>
                    )}
                    {cred?.hint && (
                      <div>
                        <span className="text-subtle-light dark:text-subtle-dark">Hint: </span>
                        <span className="font-mono text-xs text-text-light dark:text-text-dark">{cred.hint}</span>
                      </div>
                    )}
                  </div>
                  {cred?.last_used_at && (
                    <p className="text-xs text-subtle-light dark:text-subtle-dark">
                      Last used {new Date(cred.last_used_at).toLocaleString()}
                    </p>
                  )}
                  <p className="text-xs text-subtle-light dark:text-subtle-dark">
                    Encrypted with AES-256-GCM and never exposed in plaintext.
                  </p>
                </div>
              )
            })() : (
              <p className="text-sm text-subtle-light dark:text-subtle-dark">
                No credential configured. Credentials are encrypted with AES-256-GCM and never exposed in plaintext.
              </p>
            )}
          </div>

          {/* Upload (manual sources) */}
          {isManual && (
            <div className="bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] rounded-xl shadow-ambient dark:shadow-none border border-border-light dark:border-border-dark p-6">
              <h2 className="text-base font-semibold text-text-light dark:text-text-dark mb-4">
                {uploadVariant === 'capture'
                  ? 'Upload Network Capture'
                  : isBinary ? 'Upload Binary Archive' : 'Upload Source Archive'}
              </h2>
              <p className="text-sm text-subtle-light dark:text-subtle-dark mb-4">
                {uploadVariant === 'capture'
                  ? <>Upload a network capture, an archive, or a folder of captures to trigger an immediate scan.</>
                  : isBinary
                    ? <>Upload an archive or folder of compiled executables (ELF, PE, Mach-O) or shared libraries.</>
                    : <>Upload a source archive or folder to trigger an immediate scan.</>
                }
                {' '}
                <span className="text-xs">
                  {uploadVariant === 'capture' ? (
                    <>Captures: <code>.pcap</code> <code>.pcapng</code> <code>.cap</code> <code>.snoop</code> <code>.har</code> — or an archive/folder containing captures.</>
                  ) : (
                    <>Archives: <code>.zip</code> <code>.tar</code> <code>.tar.gz</code> <code>.tar.bz2</code> <code>.tar.xz</code> <code>.rar</code> <code>.7z</code></>
                  )}
                </span>
              </p>

              {/* Hidden inputs */}
              <input
                ref={fileInputRef}
                type="file"
                accept={uploadVariant === 'capture'
                  ? '.pcap,.pcapng,.cap,.snoop,.har,.zip,.tar,.tar.gz,.tgz,.tar.bz2,.tbz2,.tar.xz,.txz,.rar,.7z'
                  : '.zip,.tar,.tar.gz,.tgz,.tar.bz2,.tbz2,.tar.xz,.txz,.rar,.7z'}
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0]
                  if (file) doChunkedUpload(file)
                  if (fileInputRef.current) fileInputRef.current.value = ''
                }}
              />
              <input
                ref={folderInputRef}
                type="file"
                className="hidden"
                // @ts-expect-error — webkitdirectory is not in React's type definitions
                webkitdirectory=""
                onChange={handleFolderChange}
              />

              {/* Drop zone */}
              <div
                onDragOver={(e) => { e.preventDefault(); setIsDragging(true) }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={handleDrop}
                className={`relative flex flex-col items-center justify-center gap-2 px-6 py-10 rounded-lg border-2 border-dashed transition-colors ${
                  isUploading || isZipping
                    ? 'opacity-50 pointer-events-none border-border-light dark:border-border-dark'
                    : isDragging
                      ? 'border-primary bg-primary/5'
                      : 'border-border-light dark:border-border-dark hover:border-primary/50'
                }`}
              >
                {isZipping || isUploading ? (
                  <span className="material-icons text-3xl text-primary animate-spin">sync</span>
                ) : (
                  <span className={`material-icons text-3xl ${isDragging ? 'text-primary' : 'text-subtle-light dark:text-subtle-dark'}`}>
                    upload_file
                  </span>
                )}
                <p className="text-sm font-medium text-text-light dark:text-text-dark">
                  {isZipping
                    ? 'Compressing folder…'
                    : isUploading
                      ? 'Uploading…'
                      : uploadVariant === 'capture'
                        ? 'Drop a capture, archive, or folder here'
                        : 'Drop an archive or folder here'}
                </p>
                {!isZipping && !isUploading && (
                  <p className="text-xs text-subtle-light dark:text-subtle-dark">
                    or{' '}
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="text-primary hover:underline"
                    >
                      {uploadVariant === 'capture' ? 'browse file' : 'browse archive'}
                    </button>
                    {' / '}
                    <button
                      type="button"
                      onClick={() => folderInputRef.current?.click()}
                      className="text-primary hover:underline"
                    >
                      browse folder
                    </button>
                  </p>
                )}
              </div>

              {isUploading && uploadTotalBytes > 0 && (
                <div className="mt-3 space-y-2">
                  <div className="flex items-center justify-between text-xs text-subtle-light dark:text-subtle-dark">
                    <span>
                      {uploadBytesUploaded < uploadTotalBytes ? 'Uploading…' : 'Processing…'}
                      {' '}
                      <span className="font-mono">
                        {formatBytes(uploadBytesUploaded)} / {formatBytes(uploadTotalBytes)}
                      </span>
                    </span>
                    <div className="flex items-center gap-3">
                      <span>{Math.round((uploadBytesUploaded / uploadTotalBytes) * 100)}%</span>
                      <button
                        type="button"
                        onClick={cancelUpload}
                        className="text-error hover:underline"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                  <ProgressBar
                    value={Math.round((uploadBytesUploaded / uploadTotalBytes) * 100)}
                    animated={uploadBytesUploaded === uploadTotalBytes}
                  />
                </div>
              )}

              {uploadMsg && !isUploading && !isZipping && (
                <p className={`mt-3 text-sm flex items-center gap-1.5 ${uploadMsg.startsWith('Upload failed') || uploadMsg.startsWith('Failed') ? 'text-error' : 'text-success'}`}>
                  <span className="material-icons text-base">
                    {uploadMsg.startsWith('Upload failed') || uploadMsg.startsWith('Failed') ? 'error' : 'check_circle'}
                  </span>
                  {uploadMsg}
                </p>
              )}
            </div>
          )}

          {/* ── Import CycloneDX BOM ──────────────────────────────────────────── */}
          <div className="bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] rounded-xl shadow-ambient dark:shadow-none border border-border-light dark:border-border-dark p-6">
            <h2 className="text-base font-semibold text-text-light dark:text-text-dark mb-4">Import CycloneDX BOM</h2>
            <p className="text-sm text-subtle-light dark:text-subtle-dark mb-4">
              Manually import a CycloneDX BOM JSON file to load crypto assets and packages for this source.
            </p>

            <div className="space-y-3 mb-4">
              <div>
                <label className="block text-xs font-medium text-subtle-light dark:text-subtle-dark mb-1">Scan started at (optional)</label>
                <input
                  type="datetime-local"
                  value={bomStartedAt}
                  onChange={(e) => setBomStartedAt(e.target.value)}
                  className="w-full px-3 py-2 text-sm rounded-lg bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-subtle-light dark:text-subtle-dark mb-1">Duration in seconds (optional)</label>
                <input
                  type="number"
                  min="0"
                  value={bomDurationSec}
                  onChange={(e) => setBomDurationSec(e.target.value)}
                  disabled={!bomStartedAt}
                  placeholder="e.g. 120"
                  className="w-full px-3 py-2 text-sm rounded-lg bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark focus:outline-none focus:ring-2 focus:ring-primary/50 disabled:opacity-50"
                />
              </div>
            </div>

            <input
              ref={bomFileInputRef}
              type="file"
              accept=".json"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0]
                if (file) {
                  setImportBomStatus(null)
                  importBomMutation.mutate(file)
                }
                e.target.value = ''
              }}
            />
            <div
              onDragOver={(e) => { e.preventDefault(); setBomIsDragging(true) }}
              onDragLeave={() => setBomIsDragging(false)}
              onDrop={(e) => {
                e.preventDefault()
                setBomIsDragging(false)
                const file = e.dataTransfer.files[0]
                if (!file) return
                if (!file.name.endsWith('.json')) {
                  setImportBomStatus({ type: 'error', message: 'Please drop a .json file.' })
                  return
                }
                setImportBomStatus(null)
                importBomMutation.mutate(file)
              }}
              className={`flex flex-col items-center justify-center gap-2 p-6 rounded-xl border-2 border-dashed transition-colors ${bomIsDragging ? 'border-primary bg-primary/5' : 'border-border-light dark:border-border-dark'} ${importBomMutation.isPending ? 'opacity-50 pointer-events-none' : 'cursor-pointer hover:border-primary hover:bg-primary/5'}`}
              onClick={() => !importBomMutation.isPending && bomFileInputRef.current?.click()}
            >
              <span className="material-icons text-2xl text-subtle-light dark:text-subtle-dark">upload_file</span>
              {importBomMutation.isPending ? (
                <p className="text-sm text-subtle-light dark:text-subtle-dark flex items-center gap-1.5">
                  <span className="material-icons text-base animate-spin">sync</span>
                  Importing…
                </p>
              ) : (
                <p className="text-sm text-subtle-light dark:text-subtle-dark">
                  Drop a CycloneDX BOM file here or{' '}
                  <span className="text-primary">browse</span>
                </p>
              )}
            </div>

            {importBomStatus && (
              <div className={`mt-3 flex items-start gap-2 p-3 rounded-lg text-sm ${importBomStatus.type === 'success' ? 'bg-green-50 dark:bg-green-900/20 text-success' : 'bg-red-50 dark:bg-red-900/20 text-error'}`}>
                <span className="material-icons text-base mt-0.5">{importBomStatus.type === 'success' ? 'check_circle' : 'error'}</span>
                <span>{importBomStatus.message}</span>
              </div>
            )}
          </div>
        </div>

        {/* Scan History */}
        <div className="bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] rounded-xl shadow-ambient dark:shadow-none border border-border-light dark:border-border-dark p-6 h-fit">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold text-text-light dark:text-text-dark">
              Recent Scans
            </h2>
            <Link
              to={`/scans?source_id=${source.id}`}
              className="text-xs text-primary hover:underline"
            >
              View all
            </Link>
          </div>
          {scans.length === 0 ? (
            <div className="text-center py-8">
              <span className="material-icons text-3xl text-subtle-light dark:text-subtle-dark opacity-40">
                manage_search
              </span>
              <p className="text-sm text-subtle-light dark:text-subtle-dark mt-2">No scans yet</p>
            </div>
          ) : (
            <ul className="space-y-3">
              {scans.slice(0, 8).map((scan) => (
                <li
                  key={scan.id}
                  className="flex flex-col gap-1 p-3 bg-background-light dark:bg-background-dark rounded-lg"
                >
                  <div className="flex items-center justify-between">
                    <Link
                      to={`/scans/${scan.id}`}
                      className="font-mono text-xs text-primary hover:underline"
                    >
                      {scan.id.slice(0, 8)}…
                    </Link>
                    <ScanStatusBadge status={scan.status} />
                  </div>
                  <ProgressBar
                    value={scan.progress}
                    animated={scan.status === 'running'}
                    size="sm"
                    showLabel
                  />
                  <p className="text-xs text-subtle-light dark:text-subtle-dark">
                    {scan.trigger_type} ·{' '}
                    {scan.started_at ? new Date(scan.started_at).toLocaleString() : 'queued'}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  )
}
